import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { generateAffiliateUrl } from "@/lib/affiliateLinks";
import type { BundleItem } from "@/contracts/bundles";

interface BundleItemCardProps {
  item: BundleItem;
  retailerSlug: string;
  retailerName: string;
  onItemClick: (itemId: string) => void;
}

export function BundleItemCard({ 
  item, 
  retailerSlug, 
  retailerName,
  onItemClick 
}: BundleItemCardProps) {
  const handleClick = () => {
    onItemClick(item.id);
    const url = generateAffiliateUrl(retailerSlug, item.search_query);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="hover:border-primary/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Product Image */}
          <div className="shrink-0 w-20 h-20 rounded-md bg-muted flex items-center justify-center overflow-hidden">
            {item.image_url ? (
              <img 
                src={item.image_url} 
                alt={item.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-muted-foreground">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm mb-1 line-clamp-2">
                {item.name}
              </h4>
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                {item.description}
              </p>
              <div className="flex items-center gap-3 text-xs">
                <span className="font-medium text-foreground">
                  {item.price_range}
                </span>
                {item.category && (
                  <span className="text-muted-foreground">
                    {item.category}
                  </span>
                )}
              </div>
            </div>
            
            <Button
              size="sm"
              variant="outline"
              onClick={handleClick}
              className="shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Find on {retailerName}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
